import { motion } from "framer-motion";
import { CreditCard, Cpu, MessageSquare, Banknote } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import logo from "@/assets/logo.webp";
import DemoModal from "@/pages/Demo";

/* ── Layout constants (viewBox 1000 × 780) ─────────────────── */
const [VB_W, VB_H, CX, CY] = [1000, 780, 500, 390];
const [LAP_W, LAP_H, LAP_KB] = [280, 175, 22];
const [LAP_X, LAP_Y] = [CX - LAP_W / 2, CY - (LAP_H + LAP_KB) / 2];
const [CARD_W, CARD_H, FEAT_H] = [240, 80, 100];

const CARD_POS = { top: [CX, 160], right: [820, CY], bottom: [CX, 615], left: [180, CY] };

const LINES = {
  top: {
    x1: CARD_POS.top[0],
    y1: CARD_POS.top[1] + CARD_H / 2,
    x2: CX,
    y2: LAP_Y,
  },
  right: {
    x1: CARD_POS.right[0] - CARD_W / 2,
    y1: CARD_POS.right[1],
    x2: LAP_X + LAP_W,
    y2: CY,
  },
  bottom: {
    x1: CARD_POS.bottom[0],
    y1: CARD_POS.bottom[1] - CARD_H / 2,
    x2: CX,
    y2: LAP_Y + LAP_H + LAP_KB,
  },
  left: {
    x1: CARD_POS.left[0] + CARD_W / 2,
    y1: CARD_POS.left[1],
    x2: LAP_X,
    y2: CY,
  },
};

const getLineForModule = (module, isActive) => {
  const base = LINES[module.pos];

  if (module.pos !== "top") return base;

  // Top card now expands upward, so keep connector anchored to its lower edge.
  const currentBottomEdgeY = CARD_POS.top[1] + CARD_H / 2;

  return {
    ...base,
    y1: isActive ? currentBottomEdgeY : base.y1,
  };
};

/* ── Module data ────────────────────────────────────────────── */
const MODULES = [
  {
    id: "payments",
    pos: "top",
    Icon: CreditCard,
    color: "#10b981",
    rgb: "16,185,129",
    animType: "pulse",
    label: "Payments",
    tagline: "Integrated payment processing",
    features: ["Seamless HitPay checkout", "QR code payments", "Auto-generated invoices"],
  },
  {
    id: "messaging",
    pos: "right",
    Icon: MessageSquare,
    color: "#f97316",
    rgb: "249,115,22",
    animType: "dots",
    label: "Messaging",
    tagline: "Automated client alerts",
    features: ["Appointment reminders", "Vaccination alerts", "Follow-up messages"],
  },
  {
    id: "hr",
    pos: "bottom",
    Icon: Banknote,
    color: "#06b6d4",
    rgb: "6,182,212",
    animType: "bars",
    label: "HR & Payroll",
    tagline: "Built-in staff management",
    features: ["Attendance tracking", "Payroll automation", "Staff management"],
  },
  {
    id: "hardware",
    pos: "left",
    Icon: Cpu,
    color: "#a855f7",
    rgb: "168,85,247",
    animType: "scan",
    label: "Hardware",
    tagline: "Device & equipment sync",
    features: ["X-ray machine sync", "Lab equipment link", "Device connectivity"],
  },
];

const PTCL = [
  [80, 60, 2.5, 0, 4.2],
  [880, 85, 2, 0.9, 5.1],
  [720, 700, 3, 1.4, 3.8],
  [210, 620, 2, 0.3, 4.6],
  [530, 40, 2, 2.1, 3.5],
  [940, 420, 2.5, 1.1, 4.9],
  [55, 390, 2, 0.6, 5.4],
  [440, 740, 3, 1.7, 3.3],
  [760, 260, 2, 0.4, 4.7],
  [140, 500, 2.5, 2.4, 3.9],
  [630, 165, 2, 1.8, 4.1],
  [340, 730, 2, 0.7, 5],
];

/* ── Keyframes — declared once as a constant, injected via <style> in SVG ── */
const KF = `
  @keyframes va-orbit{to{transform:rotate(360deg)}}
  @keyframes va-orbit-r{to{transform:rotate(-360deg)}}
  @keyframes va-dash{to{stroke-dashoffset:-24}}
  @keyframes va-pulse-r{0%{r:22px;opacity:.7}100%{r:44px;opacity:0}}
  @keyframes va-scan{0%{transform:translateY(-120%)}100%{transform:translateY(220%)}}
  @keyframes va-glow-pulse{0%,100%{opacity:.05}50%{opacity:.18}}
  @keyframes va-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
`;

/* ── Micro-animations per card type ─────────────────────────── */
const MicroAnim = ({ m, x, y, on }) => {
  if (m.animType === "dots")
    return (
      <g>
        {[0, 1, 2].map((i) => (
          <motion.circle
            key={i}
            cx={x + 62 + i * 8}
            cy={y + 60}
            r={2.5}
            fill={m.color}
            animate={on ? { cy: [y + 60, y + 54, y + 60] } : { opacity: 0.2 }}
            transition={{ repeat: Infinity, duration: 0.75, delay: i * 0.2 }}
          />
        ))}
      </g>
    );
  if (m.animType === "bars")
    return (
      <g>
        {[0.5, 0.9, 0.35, 0.75, 0.6].map((h, i) => (
          <motion.rect
            key={i}
            x={x + 62 + i * 7}
            y={y + 55}
            width={4}
            height={12 * h}
            rx={1}
            fill={m.color}
            style={{ originY: "bottom", transformBox: "fill-box" }}
            animate={on ? { scaleY: [h, 1, h * 0.3, h] } : { opacity: 0.25 }}
            transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.13 }}
          />
        ))}
      </g>
    );
  if (m.animType === "scan" && on)
    return (
      <foreignObject
        x={x + 14}
        y={y + 16}
        width={38}
        height={38}
        style={{ pointerEvents: "none", overflow: "hidden" }}>
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            position: "relative",
            borderRadius: "10px",
          }}>
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              height: "1px",
              background: `linear-gradient(90deg,transparent,${m.color},transparent)`,
              animation: "va-scan 1.5s ease-in-out infinite",
            }}
          />
        </div>
      </foreignObject>
    );
  return null;
};

/* ── Module card (SVG group) ─────────────────────────────────── */
const ModuleCard = ({ m, idx, hovered, setHovered }) => {
  const on = hovered === m.id;
  const expandsUp = m.pos === "top";
  const [cx, cy] = CARD_POS[m.pos];
  const ch = on ? CARD_H + FEAT_H : CARD_H;
  const x = cx - CARD_W / 2;
  const y = cy - CARD_H / 2 - (on && expandsUp ? FEAT_H : 0);

  return (
    <motion.g
      key={m.id}
      style={{ cursor: "help" }}
      initial={{ opacity: 0, scale: 0.7 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.4 + idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(m.id)}
      onMouseLeave={() => setHovered(null)}>
      {/* Glow outline */}
      {on && (
        <rect
          x={x - 2}
          y={y - 2}
          width={CARD_W + 4}
          height={ch + 4}
          rx="16"
          fill="none"
          stroke={m.color}
          strokeWidth="1.5"
          opacity=".55"
          style={{ filter: `drop-shadow(0 0 18px ${m.color})` }}
        />
      )}

      {/* Card bg */}
      <rect
        x={x}
        y={y}
        width={CARD_W}
        height={ch}
        rx="14"
        fill="rgba(5,12,30,0.88)"
        stroke={on ? `rgba(${m.rgb},.5)` : "rgba(255,255,255,.08)"}
        strokeWidth={on ? "1.5" : "1"}
        style={{ transition: "all .35s" }}
      />

      {/* Radial fill + top shimmer */}
      {on && (
        <>
          <rect
            x={x}
            y={y}
            width={CARD_W}
            height={ch}
            rx="14"
            fill={`rgba(${m.rgb},.1)`}
            style={{ pointerEvents: "none" }}
          />
          <defs>
            <linearGradient id={`sh-${m.id}`} x1="0" y1="0" x2="1" y2="0">
              {["0%", "40%", "60%", "100%"].map((o, i) => (
                <stop
                  key={i}
                  offset={o}
                  stopColor={m.color}
                  stopOpacity={i === 0 || i === 3 ? "0" : "1"}
                />
              ))}
            </linearGradient>
          </defs>
          <line
            x1={x}
            y1={y}
            x2={x + CARD_W}
            y2={y}
            stroke={`url(#sh-${m.id})`}
            strokeWidth="1.5"
          />
        </>
      )}

      {/* Icon box */}
      <rect
        x={x + 12}
        y={y + 12}
        width={34}
        height={34}
        rx="9"
        fill={`rgba(${m.rgb},.12)`}
        stroke={`rgba(${m.rgb},.28)`}
        strokeWidth="1.2"
        style={{
          filter: on ? `drop-shadow(0 0 14px rgba(${m.rgb},.65))` : "none",
          transition: "filter .4s",
        }}
      />
      <foreignObject x={x + 12} y={y + 12} width={34} height={34} style={{ pointerEvents: "none" }}>
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <m.Icon size={18} color={m.color} strokeWidth={1.6} />
        </div>
      </foreignObject>

      {/* Pulse rings (Payments) */}
      {m.animType === "pulse" &&
        on &&
        [0, 0.95].map((d, i) => (
          <circle
            key={i}
            cx={x + 29}
            cy={y + 29}
            r="14"
            fill="none"
            stroke={m.color}
            strokeWidth="1"
            opacity=".7"
            style={{ animation: `va-pulse-r 1.9s ease-out infinite ${d}s` }}
          />
        ))}

      {/* Labels */}
      <text
        x={x + 56}
        y={y + 28}
        fill={on ? "#fff" : "rgba(255,255,255,.88)"}
        fontSize="12"
        fontWeight="800"
        fontFamily="system-ui,sans-serif"
        style={{ transition: "fill .25s", letterSpacing: ".01em" }}>
        {m.label}
      </text>
      <text
        x={x + 56}
        y={y + 44}
        fill="rgba(148,163,184,.75)"
        fontSize="9.5"
        fontFamily="system-ui,sans-serif">
        {m.tagline}
      </text>

      <MicroAnim m={m} x={x} y={y} on={on} />

      {/* Expanded features */}
      {on && (
        <>
          <line
            x1={x + 14}
            y1={y + CARD_H - 2}
            x2={x + CARD_W - 14}
            y2={y + CARD_H - 2}
            stroke={`rgba(${m.rgb},.22)`}
            strokeWidth="1"
          />
          {m.features.map((f, fi) => (
            <motion.g
              key={fi}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: fi * 0.08 }}>
              <circle cx={x + 20} cy={y + CARD_H + 12 + fi * 28} r={3.5} fill={m.color} />
              <text
                x={x + 30}
                y={y + CARD_H + 16 + fi * 28}
                fill="rgba(203,213,225,.9)"
                fontSize="9.5"
                fontWeight="500"
                fontFamily="system-ui,sans-serif">
                {f}
              </text>
            </motion.g>
          ))}
        </>
      )}
    </motion.g>
  );
};

/* ── Main component ─────────────────────────────────────────── */
const Governance = () => {
  const [hovered, setHovered] = useState(null);
  const [laptopHovered, setLaptopHovered] = useState(false);
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [started, setStarted] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (e.isIntersecting && !started) setStarted(true);
        }),
      { threshold: 0.15 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const [s1, s2] = ["Empowering the ", "Veterinary Ecosystem."];
    let i1 = 0,
      i2 = 0,
      tid;
    const type = () => {
      if (i1 < s1.length) {
        setT1(s1.slice(0, ++i1));
        tid = setTimeout(type, 40);
      } else if (i2 < s2.length) {
        setT2(s2.slice(0, ++i2));
        tid = setTimeout(type, 40);
      }
    };
    type();
    return () => clearTimeout(tid);
  }, [started]);

  return (
    <>
      <section className="relative z-10 overflow-hidden border-t border-white/10 bg-transparent px-6 py-24 md:px-12 md:py-32">
        {/* Ambient glows */}
        <div className="pointer-events-none absolute inset-0 z-0">
          {[
            "top-0 left-1/4 -translate-y-1/2 bg-blue-600/10",
            "right-0 bottom-0 translate-x-1/3 translate-y-1/3 bg-emerald-600/10",
            "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-purple-600/8",
          ].map((cls, i) => (
            <div key={i} className={`absolute h-135 w-135 rounded-full blur-[140px] ${cls}`} />
          ))}
        </div>

        <div ref={sectionRef} className="relative z-10 mx-auto w-full max-w-350">
          {/* Header */}
          <div className="mb-14 text-center md:mb-16">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6 inline-block rounded-full border border-[#93c5fd]/40 bg-white/70 px-5 py-2 text-xs font-bold tracking-widest text-[#5A7FA8] uppercase shadow-sm backdrop-blur-md">
              Platform Features
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65, delay: 0.1 }}
              className="text-4xl leading-tight font-extrabold tracking-tight text-[#1a3a5c] md:text-5xl lg:text-[3.5rem]">
              <span className="block min-h-[1.1em]">{t1 || "\u00A0"}</span>
              <span className="block min-h-[1.1em] bg-linear-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
                {t2}
                {started && t2.length < 21 && (
                  <span className="ml-1 inline-block h-[.85em] w-0.5 animate-pulse bg-blue-400 align-middle" />
                )}
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.28 }}
              className="mt-4 text-sm text-slate-500 md:text-[15px]">
              Five connected systems powering modern veterinary clinics.{" "}
              <span className="text-slate-600">Hover a module to explore.</span>
            </motion.p>
          </div>

          {/* Desktop ecosystem map */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.85, delay: 0.1 }}
            className="relative hidden w-full overflow-hidden rounded-[2.5rem] border border-white/8 lg:block"
            style={{
              aspectRatio: `${VB_W}/${VB_H}`,
              background: [
                "radial-gradient(circle at 28% 22%,rgba(46,108,255,.09),transparent 38%)",
                "radial-gradient(circle at 74% 70%,rgba(34,211,166,.08),transparent 36%)",
                "radial-gradient(circle at 50% 50%,rgba(100,60,200,.05),transparent 55%)",
                "rgba(3,8,20,.85)",
              ].join(","),
              backdropFilter: "blur(28px)",
            }}>
            {/* Dot grid */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[.42]"
              style={{
                backgroundImage:
                  "radial-gradient(circle,rgba(255,255,255,.17) 1px,transparent 1px)",
                backgroundSize: "40px 40px",
              }}
            />
            {/* Line grid */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[.13]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.04) 1px,transparent 1px)",
                backgroundSize: "80px 80px",
              }}
            />

            <svg
              className="absolute inset-0 h-full w-full"
              viewBox={`0 0 ${VB_W} ${VB_H}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ overflow: "hidden" }}>
              <defs>
                {/* Keyframes injected directly into SVG — no JS injection needed */}
                <style>{KF}</style>
                <filter id="va-lglow" x="-80%" y="-80%" width="260%" height="260%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <linearGradient id="glare" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="white" stopOpacity=".06" />
                  <stop offset="45%" stopColor="white" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Particles */}
              {PTCL.map(([px, py, s, d, dur], i) => (
                <motion.circle
                  key={i}
                  cx={px}
                  cy={py}
                  r={s / 2}
                  fill="rgba(255,255,255,.55)"
                  animate={{ cy: [py, py - 14, py + 5, py], opacity: [0.1, 0.45, 0.15, 0.1] }}
                  transition={{ repeat: Infinity, duration: dur, delay: d, ease: "easeInOut" }}
                />
              ))}

              {/* Connection lines */}
              {MODULES.map((m) => {
                const on = hovered === m.id;
                const ln = getLineForModule(m, on);
                return (
                  <g key={`ln-${m.id}`}>
                    <line
                      {...ln}
                      stroke={m.color}
                      strokeWidth={14}
                      strokeDasharray="6 6"
                      opacity={on ? 0.12 : 0}
                      style={{
                        transition: "opacity .4s",
                        animation: on ? "va-dash 1.1s linear infinite" : "none",
                      }}
                    />
                    <line
                      {...ln}
                      stroke={on ? m.color : "rgba(255,255,255,.09)"}
                      strokeWidth={on ? 2.5 : 1.2}
                      strokeDasharray="6 6"
                      style={{
                        animation: "va-dash 1.1s linear infinite",
                        filter: on ? "url(#va-lglow)" : "none",
                        transition: "stroke .35s,stroke-width .35s",
                      }}
                    />
                    {[
                      { cx: ln.x1, cy: ln.y1 },
                      { cx: ln.x2, cy: ln.y2 },
                    ].map((pt, i) => (
                      <circle
                        key={i}
                        {...pt}
                        r={5}
                        fill={m.color}
                        opacity={on ? 1 : 0.18}
                        style={{ transition: "opacity .35s" }}
                      />
                    ))}
                    {on && (
                      <motion.circle
                        r={6}
                        fill={m.color}
                        style={{ filter: `drop-shadow(0 0 7px ${m.color})` }}
                        animate={{ cx: [ln.x1, ln.x2, ln.x1], cy: [ln.y1, ln.y2, ln.y1] }}
                        transition={{ repeat: Infinity, duration: 1.7, ease: "linear" }}
                      />
                    )}
                  </g>
                );
              })}

              {/* Laptop */}
              <motion.g
                style={{ animation: "va-float 6s ease-in-out infinite", cursor: "help" }}
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                onMouseEnter={() => setLaptopHovered(true)}
                onMouseLeave={() => setLaptopHovered(false)}>
                {/* Orbit rings */}
                {[
                  [LAP_W * 0.72, 32, "#3b82f6", "va-orbit"],
                  [LAP_W * 0.42, 20, "#10b981", "va-orbit-r"],
                ].map(([r, dur, clr, anim], i) => (
                  <g
                    key={i}
                    style={{
                      transformOrigin: `${CX}px ${CY}px`,
                      animation: `${anim} ${dur}s linear infinite`,
                    }}>
                    <circle
                      cx={CX}
                      cy={CY}
                      r={r}
                      fill="none"
                      stroke="rgba(255,255,255,.06)"
                      strokeWidth="1"
                      strokeDasharray={i ? "2 5" : "3 6"}
                    />
                    <circle
                      cx={CX}
                      cy={i ? CY + r : CY - r}
                      r={i ? 3.5 : 5}
                      fill={clr}
                      style={{ filter: `drop-shadow(0 0 ${i ? 6 : 8}px ${clr}90)` }}
                    />
                  </g>
                ))}

                {/* Screen */}
                <rect
                  x={LAP_X}
                  y={LAP_Y}
                  width={LAP_W}
                  height={LAP_H}
                  rx="8"
                  fill="#04091c"
                  stroke="rgba(255,255,255,.14)"
                  strokeWidth="1.5"
                />
                <rect
                  x={LAP_X}
                  y={LAP_Y}
                  width={LAP_W}
                  height={LAP_H}
                  rx="8"
                  fill="rgba(59,130,246,.18)"
                  style={{ animation: "va-glow-pulse 4s ease-in-out infinite" }}
                />
                <image
                  href={logo}
                  x={LAP_X + LAP_W * 0.08}
                  y={LAP_Y + LAP_H * 0.08}
                  width={LAP_W * 0.84}
                  height={LAP_H * 0.84}
                  preserveAspectRatio="xMidYMid meet"
                  style={{ filter: "brightness(1.2) drop-shadow(0 0 12px rgba(59,130,246,.45))" }}
                />
                <rect x={LAP_X} y={LAP_Y} width={LAP_W} height={LAP_H} rx="8" fill="url(#glare)" />

                {/* Laptop hover description overlay */}
                {laptopHovered && (
                  <motion.g
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}>
                    {/* Screen fill */}
                    <rect
                      x={LAP_X}
                      y={LAP_Y}
                      width={LAP_W}
                      height={LAP_H}
                      rx="8"
                      fill="rgba(2,6,18,0.97)"
                    />
                    {/* Top bar */}
                    <rect
                      x={LAP_X}
                      y={LAP_Y}
                      width={LAP_W}
                      height={16}
                      rx="8"
                      fill="rgba(10,18,40,1)"
                    />
                    <rect
                      x={LAP_X + 8}
                      y={LAP_Y}
                      width={LAP_W - 16}
                      height={16}
                      fill="rgba(10,18,40,1)"
                    />
                    {[
                      ["#ff5f57", 0],
                      ["#febc2e", 10],
                      ["#28c840", 20],
                    ].map(([c, ox]) => (
                      <circle key={ox} cx={LAP_X + 9 + ox} cy={LAP_Y + 8} r={2.8} fill={c} />
                    ))}
                    {/* Address bar */}
                    <rect
                      x={LAP_X + 38}
                      y={LAP_Y + 3}
                      width={LAP_W - 50}
                      height={10}
                      rx="3"
                      fill="rgba(20,30,55,0.9)"
                    />
                    <text
                      x={LAP_X + 42}
                      y={LAP_Y + 11}
                      fill="rgba(148,163,184,.5)"
                      fontSize="5"
                      fontFamily="system-ui,sans-serif">
                      vetassist — Dashboard
                    </text>
                    {/* Sidebar */}
                    <rect
                      x={LAP_X}
                      y={LAP_Y + 16}
                      width={44}
                      height={LAP_H - 16}
                      fill="rgba(5,10,28,0.95)"
                    />
                    {[
                      ["#60a5fa", 22],
                      ["#94a3b8", 36],
                      ["#94a3b8", 50],
                      ["#94a3b8", 64],
                    ].map(([_clr, oy], i) => (
                      <rect
                        key={i}
                        x={LAP_X + 6}
                        y={LAP_Y + oy}
                        width={32}
                        height={9}
                        rx="3"
                        fill={i === 0 ? "rgba(96,165,250,.18)" : "transparent"}
                      />
                    ))}
                    {["Home", "Pets", "Appts", "Bills"].map((lbl, i) => (
                      <text
                        key={lbl}
                        x={LAP_X + 22}
                        y={LAP_Y + 29 + i * 14}
                        textAnchor="middle"
                        fill={i === 0 ? "#60a5fa" : "rgba(148,163,184,.45)"}
                        fontSize="5"
                        fontWeight={i === 0 ? "700" : "400"}
                        fontFamily="system-ui,sans-serif">
                        {lbl}
                      </text>
                    ))}
                    {/* Main content area */}
                    {/* Stat cards */}
                    {[
                      ["Appts", "128", "#10b981"],
                      ["Patients", "3.4k", "#60a5fa"],
                      ["Revenue", "₱82k", "#f97316"],
                    ].map(([lbl, val, clr], i) => (
                      <g key={lbl}>
                        <rect
                          x={LAP_X + 50 + i * 76}
                          y={LAP_Y + 20}
                          width={70}
                          height={28}
                          rx="4"
                          fill="rgba(15,23,42,0.9)"
                          stroke={`${clr}30`}
                          strokeWidth="0.8"
                        />
                        <text
                          x={LAP_X + 54 + i * 76}
                          y={LAP_Y + 30}
                          fill="rgba(148,163,184,.55)"
                          fontSize="4.5"
                          fontFamily="system-ui,sans-serif">
                          {lbl}
                        </text>
                        <text
                          x={LAP_X + 54 + i * 76}
                          y={LAP_Y + 42}
                          fill={clr}
                          fontSize="8.5"
                          fontWeight="800"
                          fontFamily="system-ui,sans-serif">
                          {val}
                        </text>
                      </g>
                    ))}
                    {/* Chart */}
                    <rect
                      x={LAP_X + 50}
                      y={LAP_Y + 54}
                      width={LAP_W - 56}
                      height={52}
                      rx="4"
                      fill="rgba(15,23,42,0.9)"
                      stroke="rgba(255,255,255,.06)"
                      strokeWidth="0.8"
                    />
                    <text
                      x={LAP_X + 55}
                      y={LAP_Y + 63}
                      fill="rgba(148,163,184,.5)"
                      fontSize="4.5"
                      fontFamily="system-ui,sans-serif">
                      Weekly Overview
                    </text>
                    {[0.35, 0.65, 0.45, 0.85, 0.55, 0.75, 0.4, 0.9, 0.5, 0.7].map((h, i) => (
                      <rect
                        key={i}
                        x={LAP_X + 55 + i * 20}
                        y={LAP_Y + 54 + 52 - 8 - h * 36}
                        width={12}
                        height={h * 36}
                        rx="2"
                        fill={`rgba(96,165,250,${0.25 + h * 0.55})`}
                      />
                    ))}

                    {/* Brief description */}
                    <text
                      x={LAP_X + 55}
                      y={LAP_Y + LAP_H - 50}
                      fill="rgba(226,232,240,.82)"
                      fontSize="9"
                      fontFamily="system-ui,sans-serif">
                      VetAssist unifies veterinary managements,
                    </text>
                    <text
                      x={LAP_X + 55}
                      y={LAP_Y + LAP_H - 35}
                      fill="rgba(226,232,240,.82)"
                      fontSize="9"
                      fontFamily="system-ui,sans-serif">
                      billing, and team operations in one place.
                    </text>

                    {/* Book Demo CTA */}
                    <g
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        setLaptopHovered(false);
                        setIsDemoOpen(true);
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        event.stopPropagation();
                        setLaptopHovered(false);
                        setIsDemoOpen(true);
                      }}>
                      <rect
                        x={CX - 48}
                        y={LAP_Y + LAP_H - 22}
                        width={96}
                        height={15}
                        rx="7"
                        fill="#10b981"
                        style={{ filter: "drop-shadow(0 0 5px rgba(16,185,129,.55))" }}
                      />
                      <text
                        x={CX}
                        y={LAP_Y + LAP_H - 12}
                        textAnchor="middle"
                        fill="#fff"
                        fontSize="6"
                        fontWeight="700"
                        fontFamily="system-ui,sans-serif"
                        letterSpacing="0.25"
                        lengthAdjust="spacingAndGlyphs"
                        textLength="72">
                        Book a Demo
                      </text>
                    </g>
                  </motion.g>
                )}

                {/* Keyboard */}
                <rect
                  x={LAP_X}
                  y={LAP_Y + LAP_H}
                  width={LAP_W}
                  height={LAP_KB}
                  fill="rgba(255,255,255,.055)"
                  stroke="rgba(255,255,255,.1)"
                  strokeWidth="1"
                />
                <rect
                  x={LAP_X}
                  y={LAP_Y + LAP_H + LAP_KB - 6}
                  width={LAP_W}
                  height={6}
                  rx="4"
                  fill="rgba(255,255,255,.04)"
                />
                <rect
                  x={CX - 32}
                  y={LAP_Y + LAP_H + 5}
                  width={64}
                  height={12}
                  rx="3"
                  fill="none"
                  stroke="rgba(255,255,255,.1)"
                  strokeWidth="1"
                />

                <text
                  x={CX}
                  y={LAP_Y + LAP_H + LAP_KB + 18}
                  textAnchor="middle"
                  fill="rgba(255,255,255,.28)"
                  fontSize="10"
                  fontWeight="700"
                  letterSpacing="3"
                  fontFamily="system-ui,sans-serif">
                  VetAssist Core
                </text>
              </motion.g>

              {/* Module cards */}
              {MODULES.map((m, idx) => (
                <ModuleCard key={m.id} m={m} idx={idx} hovered={hovered} setHovered={setHovered} />
              ))}
            </svg>
          </motion.div>

          {/* Mobile fallback */}
          <div className="space-y-10 lg:hidden">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto w-64">
              <div
                className="relative overflow-hidden"
                style={{
                  aspectRatio: "16/10",
                  borderRadius: "10px 10px 0 0",
                  background: "#030915",
                  border: "1.5px solid rgba(255,255,255,.12)",
                  borderBottom: "none",
                  padding: "8%",
                  boxShadow: "inset 0 0 40px rgba(59,130,246,.1)",
                }}>
                <img
                  src={logo}
                  alt="VetAssist"
                  className="relative z-10 h-full w-full object-contain"
                  style={{ filter: "brightness(1.2) drop-shadow(0 0 12px rgba(59,130,246,.4))" }}
                />
              </div>
              <div
                style={{
                  height: 14,
                  background: "linear-gradient(180deg,rgba(255,255,255,.08),rgba(255,255,255,.03))",
                  border: "1px solid rgba(255,255,255,.1)",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                }}
              />
              <p className="mt-2.5 text-center text-[9px] font-black tracking-[0.26em] text-black uppercase">
                VetAssist Core
              </p>
            </motion.div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {MODULES.map((m, i) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.09 }}
                  className="overflow-hidden rounded-2xl border p-4"
                  style={{
                    background: "rgba(5,12,28,.82)",
                    backdropFilter: "blur(16px)",
                    borderColor: `rgba(${m.rgb},.24)`,
                    boxShadow: `inset 0 1px 0 rgba(${m.rgb},.12)`,
                  }}>
                  <div className="mb-3 flex items-center gap-2.5">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                      style={{
                        background: `rgba(${m.rgb},.13)`,
                        border: `1.5px solid rgba(${m.rgb},.3)`,
                      }}>
                      <m.Icon size={18} color={m.color} strokeWidth={1.6} />
                    </div>
                    <div>
                      <div className="text-[11px] leading-tight font-black text-white">
                        {m.label}
                      </div>
                      <div className="mt-0.5 text-[9px] text-slate-500">{m.tagline}</div>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {m.features.map((f, fi) => (
                      <div key={fi} className="flex items-start gap-1.5">
                        <div
                          className="mt-0.75 h-1 w-1 shrink-0 rounded-full"
                          style={{ background: m.color }}
                        />
                        <span className="text-[10px] leading-snug text-slate-400">{f}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <DemoModal open={isDemoOpen} onOpenChange={setIsDemoOpen} />
    </>
  );
};

export default Governance;
